import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Users, Plus, Eye, EyeOff, Edit, Trash2, MapPin, Award, Phone, Mail, Clock, List, Grid3X3, Lock, Unlock, Send, KeyRound } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';

const UsersPage = () => {
  const { hasRole } = useAuth();
  const { plants, locations } = useData();
  const [users, setUsers] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeView, setActiveView] = useState('table');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    username: '',
    role: 'operator',
    phone: '',
    function: '',
    address: '',
    locality: '',
    country: 'Romania',
    language: 'ro',
    hourly_cost: '',
    plant_access: [],
    location_access: [],
    competencies: [],
    is_active: true,
    works_in_shifts: false
  });

  useEffect(() => {
    fetchUsers();
    fetchCompetencies();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Eroare la incarcarea utilizatorilor');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetencies = async () => {
    try {
      const response = await api.get('/competencies');
      setCompetencies(response.data);
    } catch (error) {
      console.error('Error fetching competencies:', error);
    }
  };

  const [sendWelcomeOnCreate, setSendWelcomeOnCreate] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        hourly_cost: formData.hourly_cost ? parseFloat(formData.hourly_cost) : null
      };
      
      if (editUser) {
        const passwordChanged = !!payload.password;
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editUser.id}`, payload);
        toast.success('Utilizator actualizat!');
        
        // If password was changed, offer to send reset email
        if (passwordChanged) {
          try {
            await api.post(`/users/${editUser.id}/admin-reset-password`, {
              user_id: editUser.id,
              new_password: formData.password,
              send_email: true
            });
            toast.success(`Email cu noua parola trimis catre ${formData.email}`);
          } catch {}
        }
      } else {
        const res = await api.post('/users', payload);
        toast.success('Utilizator creat cu succes!');
        
        // Auto-send welcome email with the password from form
        if (sendWelcomeOnCreate && res.data?.id) {
          try {
            await api.post(`/users/${res.data.id}/send-welcome`, {
              user_id: res.data.id,
              password: formData.password
            });
            toast.success(`Email de bun venit trimis catre ${formData.email}`);
          } catch (emailErr) {
            toast.warning('Utilizator creat, dar emailul nu a putut fi trimis');
          }
        }
      }
      setShowAddDialog(false);
      setEditUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setFormData({
      email: user.email || '',
      password: '',
      full_name: user.full_name || '',
      username: user.username || '',
      role: user.role || 'operator',
      phone: user.phone || '',
      function: user.function || '',
      address: user.address || '',
      locality: user.locality || '',
      country: user.country || 'Romania',
      language: user.language || 'ro',
      hourly_cost: user.hourly_cost || '',
      plant_access: user.plant_access || [],
      location_access: user.location_access || [],
      competencies: user.competencies || [],
      is_active: user.is_active !== false,
      works_in_shifts: user.works_in_shifts || false
    });
    setCurrentStep(1);
    setShowAddDialog(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sigur doriti sa dezactivati acest utilizator?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Utilizator dezactivat!');
      fetchUsers();
    } catch (error) {
      toast.error('Eroare la dezactivare');
    }
  };

  const handleLockToggle = async (user) => {
    try {
      const isLocked = user.locked_until && new Date(user.locked_until) > new Date();
      if (isLocked) {
        await api.post(`/users/${user.id}/unlock`);
        toast.success(`Contul lui ${user.full_name || user.email} a fost deblocat!`);
      } else {
        if (!window.confirm(`Sigur doriti sa blocati contul lui ${user.full_name || user.email}?`)) return;
        await api.post(`/users/${user.id}/lock`);
        toast.success(`Contul lui ${user.full_name || user.email} a fost blocat!`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la operatia de blocare/deblocare');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '', password: '', full_name: '', username: '', role: 'operator',
      phone: '', function: '', address: '', locality: '', country: 'Romania',
      language: 'ro', hourly_cost: '', plant_access: [], location_access: [],
      competencies: [], is_active: true, works_in_shifts: false
    });
    setCurrentStep(1);
  };

  const [sendingWelcome, setSendingWelcome] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);

  const handleSendWelcome = async (user) => {
    // For existing users, generate a temporary password and send welcome email
    const tempPwd = `Maintix${Math.random().toString(36).slice(2, 8)}!`;
    if (!window.confirm(`Trimite email de bun venit catre ${user.full_name || user.email}?\n\nO noua parola temporara va fi generata si trimisa.`)) return;
    setSendingWelcome(true);
    try {
      // Reset password and send welcome
      await api.post(`/users/${user.id}/admin-reset-password`, {
        user_id: user.id,
        new_password: tempPwd,
        send_email: false
      });
      await api.post(`/users/${user.id}/send-welcome`, {
        user_id: user.id,
        password: tempPwd
      });
      toast.success(`Email de bun venit trimis catre ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la trimiterea emailului');
    } finally {
      setSendingWelcome(false);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetTargetUser || !resetPasswordValue) return;
    setSendingReset(true);
    try {
      const res = await api.post(`/users/${resetTargetUser.id}/admin-reset-password`, {
        user_id: resetTargetUser.id,
        new_password: resetPasswordValue,
        send_email: true
      });
      toast.success(res.data.message);
      setShowResetDialog(false);
      setResetPasswordValue('');
      setResetTargetUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la resetarea parolei');
    } finally {
      setSendingReset(false);
    }
  };

  const togglePlantAccess = (plantId) => {
    if (formData.plant_access.includes(plantId)) {
      setFormData({
        ...formData,
        plant_access: formData.plant_access.filter(id => id !== plantId),
        location_access: formData.location_access.filter(locId => {
          const loc = locations.find(l => l.id === locId);
          return loc && loc.plant_id !== plantId;
        })
      });
    } else {
      setFormData({ ...formData, plant_access: [...formData.plant_access, plantId] });
    }
  };

  const toggleLocationAccess = (locationId) => {
    if (formData.location_access.includes(locationId)) {
      setFormData({ ...formData, location_access: formData.location_access.filter(id => id !== locationId) });
    } else {
      setFormData({ ...formData, location_access: [...formData.location_access, locationId] });
    }
  };

  const toggleCompetency = (compId) => {
    if (formData.competencies.includes(compId)) {
      setFormData({ ...formData, competencies: formData.competencies.filter(id => id !== compId) });
    } else {
      setFormData({ ...formData, competencies: [...formData.competencies, compId] });
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator', manager: 'Manager', technician: 'Tehnician',
      section_chief: 'Sef Sectie', auditor: 'Auditor',
      operator: 'Operator', procurement: 'Achizitii', financial: 'Financiar', viewer: 'Vizualizator'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700', manager: 'bg-blue-100 text-blue-700',
      technician: 'bg-green-100 text-green-700', section_chief: 'bg-indigo-100 text-indigo-700',
      auditor: 'bg-emerald-100 text-emerald-700',
      operator: 'bg-yellow-100 text-yellow-700',
      procurement: 'bg-purple-100 text-purple-700', financial: 'bg-cyan-100 text-cyan-700',
      viewer: 'bg-slate-100 text-slate-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  // Prepare data for DataTable
  const usersData = users.map(u => ({
    ...u,
    role_display: getRoleLabel(u.role),
    status_display: u.is_active !== false ? 'Activ' : 'Inactiv',
    competencies_count: u.competency_names?.length || 0,
    last_login_display: u.last_login ? new Date(u.last_login).toLocaleDateString('ro-RO') : '-'
  }));

  // Columns for DataTable
  const tableColumns = [
    {
      key: 'full_name',
      label: 'Nume',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0052CC] flex items-center justify-center text-white font-semibold">
            {value?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-medium">{value}</p>
            <p className="text-xs text-slate-500">{row.username}</p>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      filterable: true,
      render: (value) => (
        <span className="flex items-center gap-1 text-sm">
          <Mail className="w-3 h-3 text-slate-400" />
          {value}
        </span>
      )
    },
    {
      key: 'phone',
      label: 'Telefon',
      sortable: true,
      filterable: true,
      render: (value) => value ? (
        <span className="flex items-center gap-1 text-sm">
          <Phone className="w-3 h-3 text-slate-400" />
          {value}
        </span>
      ) : <span className="text-slate-400">-</span>
    },
    {
      key: 'role_display',
      label: 'Rol',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <Badge className={getRoleColor(row.role)}>{value}</Badge>
      )
    },
    {
      key: 'function',
      label: 'Functie',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span>{value || <span className="text-slate-400">-</span>}</span>
          {row.works_in_shifts && (
            <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">
              <Clock className="w-3 h-3 mr-0.5" />Schimburi
            </Badge>
          )}
        </div>
      )
    },
    {
      key: 'status_display',
      label: 'Status',
      sortable: true,
      filterable: true,
      width: 'w-24',
      render: (value, row) => (
        <Badge variant={row.is_active !== false ? 'default' : 'secondary'}>
          {value}
        </Badge>
      )
    },
    {
      key: 'last_login_display',
      label: 'Ultima conectare',
      sortable: true,
      filterable: false,
      render: (value) => (
        <span className="flex items-center gap-1 text-sm text-slate-500">
          <Clock className="w-3 h-3" />
          {value}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      filterable: false,
      width: 'w-48',
      render: (_, row) => hasRole('admin') ? (
        <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} data-testid={`edit-user-${row.id}`} title="Editeaza">
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleSendWelcome(row)} disabled={sendingWelcome} title="Trimite email bun venit" className="text-green-600" data-testid={`welcome-user-${row.id}`}>
            <Send className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setResetTargetUser(row); setResetPasswordValue(''); setShowResetDialog(true); }} title="Reseteaza parola" className="text-amber-600" data-testid={`reset-pwd-user-${row.id}`}>
            <KeyRound className="w-4 h-4" />
          </Button>
          {(() => {
            const isLocked = row.locked_until && new Date(row.locked_until) > new Date();
            return (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLockToggle(row)}
                className={isLocked ? "text-orange-600" : "text-slate-400"}
                title={isLocked ? "Deblocheaza cont" : "Blocheaza cont"}
                data-testid={`lock-user-${row.id}`}
              >
                {isLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </Button>
            );
          })()}
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600" data-testid={`delete-user-${row.id}`} title="Dezactiveaza">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : null
    }
  ];

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Utilizatori</h1>
          <p className="text-slate-600 mt-1">Gestioneaza utilizatorii sistemului ({users.length} total)</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={activeView} onValueChange={setActiveView} className="mr-2">
            <TabsList className="h-10">
              <TabsTrigger value="table" className="px-3"><List className="w-4 h-4" /></TabsTrigger>
              <TabsTrigger value="cards" className="px-3"><Grid3X3 className="w-4 h-4" /></TabsTrigger>
            </TabsList>
          </Tabs>
          {hasRole('admin') && (
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) { setEditUser(null); resetForm(); }
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary h-12" data-testid="add-user-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  UTILIZATOR NOU
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editUser ? 'Editeaza Utilizator' : 'Creaza Utilizator Nou'}</DialogTitle>
                  <DialogDescription>Completeaza datele utilizatorului in 4 pasi</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <Tabs value={`step-${currentStep}`} onValueChange={(v) => setCurrentStep(parseInt(v.split('-')[1]))}>
                    <TabsList className="grid w-full grid-cols-4 mb-4">
                      <TabsTrigger value="step-1">1. Date</TabsTrigger>
                      <TabsTrigger value="step-2">2. Rol</TabsTrigger>
                      <TabsTrigger value="step-3">3. Locatii</TabsTrigger>
                      <TabsTrigger value="step-4">4. Competente</TabsTrigger>
                    </TabsList>

                    {/* Step 1 */}
                    <TabsContent value="step-1" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Username *</Label>
                          <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="h-11" data-testid="user-username-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Parola {editUser ? '(gol = neschimbat)' : '*'}</Label>
                          <div className="relative">
                            <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editUser} className="h-11 pr-10" minLength={5} />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nume *</Label>
                          <Input value={formData.full_name.split(' ')[0] || ''} onChange={(e) => { const lastName = formData.full_name.split(' ').slice(1).join(' '); setFormData({ ...formData, full_name: `${e.target.value} ${lastName}`.trim() }); }} required className="h-11" />
                        </div>
                        <div className="space-y-2">
                          <Label>Prenume *</Label>
                          <Input value={formData.full_name.split(' ').slice(1).join(' ') || ''} onChange={(e) => { const firstName = formData.full_name.split(' ')[0] || ''; setFormData({ ...formData, full_name: `${firstName} ${e.target.value}`.trim() }); }} required className="h-11" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="h-11" data-testid="user-email-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Telefon</Label>
                          <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="h-11" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Functie</Label>
                          <Input value={formData.function} onChange={(e) => setFormData({ ...formData, function: e.target.value })} className="h-11" />
                        </div>
                        <div className="space-y-2">
                          <Label>Cost ora (LEI)</Label>
                          <Input type="number" value={formData.hourly_cost} onChange={(e) => setFormData({ ...formData, hourly_cost: e.target.value })} className="h-11" min="0" step="0.01" />
                        </div>
                      </div>
                      <div className="flex justify-end pt-4">
                        <Button type="button" onClick={() => setCurrentStep(2)}>Urmatorul pas →</Button>
                      </div>
                    </TabsContent>

                    {/* Step 2 */}
                    <TabsContent value="step-2" className="space-y-4">
                      <div className="space-y-2">
                        <Label>Selecteaza rolul *</Label>
                        <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                          {[
                            { value: 'admin', label: 'Administrator', desc: 'Acces complet' },
                            { value: 'manager', label: 'Manager Mentenanta', desc: 'Gestionare tichete, revizii' },
                            { value: 'section_chief', label: 'Sef Sectie', desc: 'Portal sef sectie, rapoarte' },
                            { value: 'auditor', label: 'Auditor', desc: 'Verificare echipamente, audit mentenanta' },
                            { value: 'technician', label: 'Tehnician', desc: 'Executie revizii' },
                            { value: 'operator', label: 'Operator', desc: 'Creare tichete' },
                            { value: 'procurement', label: 'Achizitii', desc: 'Cereri materiale' },
                            { value: 'financial', label: 'Financiar', desc: 'Rapoarte costuri' },
                            { value: 'viewer', label: 'Vizualizator', desc: 'Doar vizualizare' }
                          ].map((role) => (
                            <label key={role.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${formData.role === role.value ? 'border-[#0052CC] bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                              <input type="radio" name="role" value={role.value} checked={formData.role === role.value} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="mt-1" />
                              <div>
                                <p className="font-medium">{role.label}</p>
                                <p className="text-sm text-slate-500">{role.desc}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                      {/* Works in shifts toggle */}
                      <div className="flex items-center justify-between p-3 mt-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">Lucreaza in schimburi</p>
                            <p className="text-xs text-blue-600">Apare in planificarea schimburilor</p>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.works_in_shifts}
                          onChange={(e) => setFormData({ ...formData, works_in_shifts: e.target.checked })}
                          className="w-5 h-5 accent-blue-600"
                          data-testid="works-in-shifts-toggle"
                        />
                      </div>
                      <div className="flex justify-between pt-4">
                        <Button type="button" variant="outline" onClick={() => setCurrentStep(1)}>← Inapoi</Button>
                        <Button type="button" onClick={() => setCurrentStep(3)}>Urmatorul pas →</Button>
                      </div>
                    </TabsContent>

                    {/* Step 3 */}
                    <TabsContent value="step-3" className="space-y-4">
                      <div className="space-y-2">
                        <Label>Acces la sedii si locatii:</Label>
                        <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-3">
                          {plants.map((plant) => {
                            const plantLocations = locations.filter(l => l.plant_id === plant.id);
                            const isPlantSelected = formData.plant_access.includes(plant.id);
                            return (
                              <div key={plant.id} className="space-y-2">
                                <label className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${isPlantSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                                  <input type="checkbox" checked={isPlantSelected} onChange={() => togglePlantAccess(plant.id)} className="w-4 h-4" />
                                  <span className="font-medium">{plant.name}</span>
                                </label>
                                {isPlantSelected && plantLocations.length > 0 && (
                                  <div className="ml-6 space-y-1">
                                    {plantLocations.map((loc) => (
                                      <label key={loc.id} className="flex items-center gap-2 p-1 cursor-pointer hover:bg-slate-50 rounded">
                                        <input type="checkbox" checked={formData.location_access.includes(loc.id)} onChange={() => toggleLocationAccess(loc.id)} className="w-4 h-4" />
                                        <MapPin className="w-4 h-4 text-slate-400" />
                                        <span className="text-sm">{loc.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex justify-between pt-4">
                        <Button type="button" variant="outline" onClick={() => setCurrentStep(2)}>← Inapoi</Button>
                        <Button type="button" onClick={() => setCurrentStep(4)}>Urmatorul pas →</Button>
                      </div>
                    </TabsContent>

                    {/* Step 4 */}
                    <TabsContent value="step-4" className="space-y-4">
                      <div className="space-y-2">
                        <Label>Competente:</Label>
                        <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-1">
                          {competencies.map((comp) => (
                            <label key={comp.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${formData.competencies.includes(comp.id) ? 'bg-green-50' : 'hover:bg-slate-50'}`}>
                              <input type="checkbox" checked={formData.competencies.includes(comp.id)} onChange={() => toggleCompetency(comp.id)} className="w-4 h-4" />
                              <Award className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{comp.name}</span>
                            </label>
                          ))}
                          {competencies.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Nu exista competente definite</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4" />
                        <Label htmlFor="is_active">Utilizator activ</Label>
                      </div>
                      {!editUser && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <input type="checkbox" id="send_welcome" checked={sendWelcomeOnCreate} onChange={(e) => setSendWelcomeOnCreate(e.target.checked)} className="w-4 h-4 accent-green-600" />
                          <Label htmlFor="send_welcome" className="flex items-center gap-2 cursor-pointer text-green-800">
                            <Send className="w-4 h-4" /> Trimite email de bun venit cu datele de autentificare
                          </Label>
                        </div>
                      )}
                      {editUser && formData.password && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <Mail className="w-4 h-4 text-amber-600" />
                          <p className="text-sm text-amber-800">La salvare, noua parola va fi trimisa pe email catre utilizator</p>
                        </div>
                      )}
                      <div className="flex justify-between pt-4">
                        <Button type="button" variant="outline" onClick={() => setCurrentStep(3)}>← Inapoi</Button>
                        <Button type="submit" className="btn-primary" data-testid="save-user-btn">{editUser ? 'ACTUALIZEAZA' : 'SALVEAZA'}</Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : activeView === 'table' ? (
        <DataTable
          data={usersData}
          columns={tableColumns}
          pageSize={10}
          searchable={true}
          exportable={true}
          printable={true}
          selectable={true}
          onRowClick={hasRole('admin') ? handleEdit : undefined}
          emptyMessage="Nu exista utilizatori"
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {usersData.length > 0 ? usersData.map((u) => (
            <Card key={u.id} className="card-industrial" data-testid={`user-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#0052CC] flex items-center justify-center text-white font-semibold text-lg">
                      {u.full_name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{u.full_name}</p>
                        {!u.is_active && <Badge variant="secondary">Inactiv</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span>
                      </div>
                      {u.competency_names?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {u.competency_names.slice(0, 2).map((comp, i) => (
                            <Badge key={i} variant="outline" className="text-xs"><Award className="w-3 h-3 mr-1" />{comp}</Badge>
                          ))}
                          {u.competency_names.length > 2 && <Badge variant="outline" className="text-xs">+{u.competency_names.length - 2}</Badge>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={getRoleColor(u.role)}>{getRoleLabel(u.role)}</Badge>
                    {hasRole('admin') && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} title="Editeaza"><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSendWelcome(u)} className="text-green-600" title="Email bun venit"><Send className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => { setResetTargetUser(u); setResetPasswordValue(''); setShowResetDialog(true); }} className="text-amber-600" title="Reseteaza parola"><KeyRound className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-red-600" title="Dezactiveaza"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="card-industrial col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500">Nu exista utilizatori</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Resetare Parola</DialogTitle>
            <DialogDescription>Reseteaza parola pentru {resetTargetUser?.full_name || resetTargetUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Parola noua</Label>
              <Input
                type="text"
                value={resetPasswordValue}
                onChange={(e) => setResetPasswordValue(e.target.value)}
                placeholder="Introduceti parola noua..."
                data-testid="reset-password-input"
              />
              <p className="text-xs text-slate-500">Parola va fi trimisa pe email catre {resetTargetUser?.email}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Anuleaza</Button>
            <Button onClick={handleAdminResetPassword} disabled={!resetPasswordValue || sendingReset} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-reset-password-btn">
              {sendingReset ? 'Se trimite...' : 'Reseteaza si Trimite Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
